<script lang="ts">
    import {
        VpEditIcon,
        VpEqualizerIcon,
        VpSearchIcon,
    } from '@decathlon-experience/icons/svelte';
    import { createDialog } from '@melt-ui/svelte';
    import { writable } from 'svelte/store';
    import { fade } from 'svelte/transition';

    import cn from '$common/utils/cn';
    import viewport from '$saga/actions/useViewport';
    import { flyDrawer } from '$saga/utils/customTransitions';
    import FiltersBar from '$saga/views/Search/MainFilters/FiltersBar.svelte';
    import FiltersDrawerContent from '$saga/views/Search/MainFilters/FiltersDrawerContent.svelte';
    import {
        filtersContext,
        isSecondaryFiltersOpen,
    } from '$saga/views/Search/store';
    import { FilterType } from '$saga/views/Search/types';

    let isSticky = false;
    let selectedFilterType = FilterType.SELECTION;
    let isModalOpen = writable(false);
    let menuTitle = 'Rechercher';
    let drawerContentRef: FiltersDrawerContent | null = null;

    const handleSelectedFilter = async (filterType: FilterType) => {
        selectedFilterType = filterType;
        switch (filterType) {
            case FilterType.SELECTION:
                menuTitle = 'Rechercher';
                break;
            case FilterType.DESTINATION:
                menuTitle = 'Destination';
                break;
            case FilterType.SPORT:
                menuTitle = 'Sport';
                break;
            case FilterType.PAX:
                menuTitle = 'Voyageurs';
                break;
            case FilterType.STARTDATE:
                menuTitle = 'Date de départ';
                break;
        }
    };

    const resetView = () => {
        selectedFilterType = FilterType.SELECTION;
        menuTitle = 'Rechercher';
    };

    const {
        elements: { trigger, title, overlay, content, close, portalled },
        states: { open },
    } = createDialog({
        open: isModalOpen,
        forceVisible: true,
        onOutsideClick() {
            if (!$filtersContext.isHomePage && $filtersContext.isDirty) {
                drawerContentRef?.validateSelectionOnOutsideClick();
                // PLP only
                // auto submit form
                // when the user clicks outside the modal
                const form = document.getElementById(
                    'travel-search-engine-form',
                );
                if (form) {
                    setTimeout(() => {
                        form.dispatchEvent(
                            new Event('submit', {
                                bubbles: true,
                                cancelable: true,
                            }),
                        );
                    }, 0);
                }
            }
        },
    });
</script>

{#if !$filtersContext.isHomePage}
    <div
        use:viewport
        on:enterViewport={() => {
            isSticky = false;
        }}
        on:exitViewport={() => {
            isSticky = true;
        }}
    />
    {#if isSticky}
        <div class={'h-[54px] desktop:hidden'} />
    {/if}
{/if}

<div
    class={cn(
        'relative flex gap-xs p-l transition-all desktop:hidden',
        isSticky &&
            !$filtersContext.isHomePage &&
            'fixed top-0 z-10 w-full bg-container-quiet',
    )}
>
    {#if $filtersContext.isHomePage}
        <div
            class="absolute inset-0 z-10 -translate-y-1/2 scale-y-[2] transform bg-gradient-to-t from-container-quiet to-transparent py-m"
        ></div>
    {/if}

    <button
        class={cn(
            'relative top-0 z-20 flex h-12 w-full transform items-center justify-center gap-m rounded-full  px-l py-s',
            !$filtersContext.isHomePage &&
                'border border-solid border-border-quiet bg-white',
            $filtersContext.isHomePage &&
                'vp-button absolute bottom-0 left-0 mx-l w-[calc(100%_-_40px)] -translate-y-1/2 rounded-none drop-shadow-lg',
        )}
        {...$trigger}
        use:trigger
    >
        {#if $filtersContext.isHomePage}
            <VpSearchIcon class="size-l" />
            Commencer ma recherche
        {:else}
            <VpEditIcon class="size-l" />
            Modifier ma recherche
        {/if}
    </button>
    {#if !$filtersContext.isHomePage}
        <button
            class={cn(
                'rounded-full border border-solid border-border-quiet bg-white px-l py-s',
            )}
            type="button"
            on:click={() => {
                $isSecondaryFiltersOpen = true;
            }}
        >
            <VpEqualizerIcon class="size-l" />
        </button>
    {/if}
</div>

<FiltersBar {trigger} {handleSelectedFilter} />

{#if $open}
    <div {...$portalled} use:portalled>
        <div
            {...$overlay}
            use:overlay
            class="fixed inset-0 z-40 bg-black/50"
            transition:fade={{ duration: 250 }}
        />
        <div
            {...$content}
            use:content
            class="fixed inset-x-0 bottom-0 z-50 flex h-full max-h-[calc(100%)] w-full flex-col bg-white transition-all duration-500 desktop:inset-x-[unset] desktop:right-0 desktop:top-0 desktop:min-w-[33vw] desktop:max-w-[33vw]"
            transition:flyDrawer
        >
            <FiltersDrawerContent
                bind:this={drawerContentRef}
                {selectedFilterType}
                {handleSelectedFilter}
                {menuTitle}
                {title}
                {close}
                {resetView}
            />
        </div>
    </div>
{/if}
